/**
 * The ledger's pills (TMMAPP-0108; audit 06 § A8, § Overrides 2; v2 L357–372). Each part of a line
 * is one pill in the Envelope column, carrying its envelope (or Unassigned) and its own category —
 * plus its amount when the line is split. The Category column lists the line's distinct categories,
 * with amounts when there are several. A part with `contra` (MM-D-128) shows a "Refund" /
 * "Give-back" `micro` tag on its pill and on its category chip; amounts stay signed.
 *
 * - Envelope pill: the envelope's data-colour `soft` wash behind `text` (a guaranteed pair), radius
 *   10, padding 2 8, a 6px dot in its `line` colour.
 * - Unassigned pill: a dashed `text3` boundary (decorative), `text2` text, radius 10.
 * - Category chip: a 1px `line` border, radius 3, padding 1 7, 11px, a 6px square swatch.
 * - Uncategorized: a dotted `text2` border.
 */
import type { ReactNode } from 'react';
import { MoneyParts } from '@/components/MoneyText';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AllocationContra } from '@/api/transactions';
import type { VirtualAccountRegisterPart } from '@/api/registers';
import { categoricalFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import type { LineCategory } from './lineCategories';

function ContraTag({ contra }: { contra: AllocationContra | null }) {
  const { t } = useTranslation();
  if (contra === null) return null;
  return (
    <Typography component="span" variant="micro" data-contra={contra} sx={{ color: 'inherit' }}>
      {t(`allocate.contra.${contra}`)}
    </Typography>
  );
}

const PILL = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  maxWidth: '100%',
  fontSize: 11,
  lineHeight: '16px',
  whiteSpace: 'nowrap',
} as const;

function Dot({ colour, square = false }: { colour: string; square?: boolean }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        flex: '0 0 6px',
        width: 6,
        height: 6,
        borderRadius: square ? '1px' : '50%',
        bgcolor: colour,
      }}
    />
  );
}

/** "{envelope or Unassigned} · {category}[ ±amount]" for one part. */
export function PartPill({
  part,
  categoryName,
  showAmount,
  currency,
}: {
  part: VirtualAccountRegisterPart;
  categoryName: string | null;
  showAmount: boolean;
  currency: string | null;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const where = part.unassigned ? t('virtualAccount.unassigned') : part.envelopeName;
  const text = [where, categoryName ?? t('envelope.uncategorized')].join(' · ');
  const amount =
    showAmount && part.amountMinor !== null ? (
      <>
        {' '}
        <MoneyParts minor={part.amountMinor} currency={currency} signed />
      </>
    ) : null;
  const body: ReactNode = (
    <>
      {text}
      {amount}
      <ContraTag contra={part.contra} />
    </>
  );
  if (part.unassigned) {
    return (
      <Box
        component="span"
        data-part-pill="unassigned"
        sx={{
          ...PILL,
          border: `1px dashed ${tokens.text3}`,
          color: tokens.text2,
          borderRadius: '10px',
          p: '1px 8px',
        }}
      >
        {body}
      </Box>
    );
  }
  const colour = categoricalFor(part.envelopeId ?? 0, tokens);
  return (
    <Box
      component="span"
      data-part-pill={part.envelopeId ?? ''}
      sx={{
        ...PILL,
        bgcolor: colour.soft,
        color: tokens.text,
        borderRadius: '10px',
        p: '2px 8px',
      }}
    >
      <Dot colour={colour.line} />
      {body}
    </Box>
  );
}

/** A category chip in the Category column (dotted "Uncategorized" when `categoryId` is null). */
export function CategoryTag({
  row,
  name,
  showAmount,
  currency,
}: {
  row: LineCategory;
  name: string | null;
  showAmount: boolean;
  currency: string | null;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const uncategorized = row.categoryId === null;
  const amount = showAmount ? (
    <>
      {' '}
      <MoneyParts minor={row.amountMinor} currency={currency} signed />
    </>
  ) : null;
  return (
    <Box
      component="span"
      data-category-tag={uncategorized ? 'uncategorized' : row.categoryId}
      sx={{
        ...PILL,
        border: uncategorized ? `1px dotted ${tokens.text2}` : `1px solid ${tokens.line}`,
        color: uncategorized ? tokens.text2 : tokens.text,
        borderRadius: '3px',
        p: '1px 7px',
      }}
    >
      {!uncategorized && <Dot colour={categoricalFor(row.categoryId ?? 0, tokens).fill} square />}
      {uncategorized ? t('envelope.uncategorized') : (name ?? '—')}
      {amount}
      <ContraTag contra={row.contra} />
    </Box>
  );
}
